import type { AuraKind } from './Aura';
import type { Pose } from './Illustration';
import type { PropKind } from './props';

export type Area =
  | 'Recordings'
  | 'Onboarding'
  | 'CoBrowse'
  | 'Spot'
  | 'Armada'
  | 'Product Analytics'
  | 'Data Management'
  | 'Preferences'
  | 'Everywhere';

export interface EmptyEntry {
  id: string;
  area: Area;
  where: string;
  title: string;
  size: 'page' | 'compact';
  subject: string;
  prop?: PropKind | null;
  pose?: Pose;
  side?: 'left' | 'right';
  aura?: AuraKind;
  robot?: false;
  note?: string;
}

export const EMPTY_STATES: EmptyEntry[] = [
  {
    id: 'sessions-none',
    area: 'Recordings',
    where: 'Sessions, a new project',
    title: 'No sessions yet',
    size: 'page',
    subject: 'sessions',
  },
  {
    id: 'sessions-bookmarks',
    area: 'Recordings',
    where: 'Bookmarks',
    title: 'Nothing bookmarked yet',
    size: 'page',
    subject: 'bookmarks',
  },
  {
    id: 'sessions-nomatch',
    area: 'Recordings',
    where: 'Sessions, a filter with no result',
    title: 'No sessions match',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'session-not-found',
    area: 'Recordings',
    where: 'A session link that resolves to nothing',
    title: 'This session is not here',
    size: 'page',
    subject: 'noMatch',
    pose: 'wait',
  },
  {
    id: 'segments-none',
    robot: false,
    area: 'Recordings',
    where: 'Segments',
    title: 'No saved segments',
    size: 'page',
    subject: 'segments',
  },
  {
    id: 'issue-strip',
    area: 'Armada',
    where: 'An issue, its sessions strip',
    title: 'No session matches that',
    size: 'compact',
    subject: 'noMatch',
  },

  {
    id: 'onb-listening',
    area: 'Onboarding',
    where: 'Setup checklist, tracker installed',
    title: 'Listening for the first session',
    size: 'page',
    subject: 'listening',
  },
  {
    id: 'onb-none',
    area: 'Onboarding',
    where: 'Setup checklist, nothing installed',
    title: 'No sessions yet',
    size: 'page',
    subject: 'sessions',
  },

  {
    id: 'cobrowse-none',
    area: 'CoBrowse',
    where: 'CoBrowse, nobody live',
    title: 'No one is live right now',
    size: 'page',
    subject: 'cobrowse',
  },
  {
    id: 'cobrowse-nomatch',
    area: 'CoBrowse',
    where: 'CoBrowse, a filter',
    title: 'No live sessions match this filter',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'cobrowse-videos',
    area: 'CoBrowse',
    where: 'CoBrowse, recorded videos',
    title: 'No videos have been recorded in your co-browsing sessions.',
    size: 'page',
    subject: 'recordedVideos',
  },

  {
    id: 'spot-none',
    area: 'Spot',
    where: 'Spot',
    title: 'No Spots yet',
    size: 'page',
    subject: 'spot',
  },
  {
    id: 'spot-window',
    area: 'Spot',
    where: 'Spot, a date window',
    title: 'No Spots in this window',
    size: 'page',
    subject: 'calendar',
  },
  {
    id: 'spot-nomatch',
    area: 'Spot',
    where: 'Spot, a filter',
    title: 'Nothing matches',
    size: 'page',
    subject: 'noMatch',
  },

  {
    id: 'issues-none',
    area: 'Armada',
    where: 'Issues',
    title: 'No issues found yet',
    size: 'page',
    subject: 'issues',
  },
  {
    id: 'issues-nomatch',
    area: 'Armada',
    where: 'Issues, filters',
    title: 'No issues match these filters',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'issues-critical',
    area: 'Armada',
    where: 'Issues, Critical to me',
    title: 'Nothing is critical to you yet',
    size: 'page',
    subject: 'critical',
  },
  {
    id: 'tests-none',
    area: 'Armada',
    where: 'Synthetics, before the first test',
    title: 'Watching your sessions',
    size: 'page',
    subject: 'tests',
  },
  {
    id: 'tests-nomatch',
    area: 'Armada',
    where: 'Synthetics, filters',
    title: 'No tests match these filters',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'tests-tab',
    area: 'Armada',
    where: 'Synthetics, an empty tab',
    title: 'No tests here',
    size: 'page',
    subject: 'tests',
  },
  {
    id: 'runs-none',
    area: 'Armada',
    where: 'Synthetics, Runs',
    title: 'Nothing has run yet',
    size: 'page',
    subject: 'runs',
  },
  {
    id: 'runs-nomatch',
    area: 'Armada',
    where: 'Synthetics, Runs filters',
    title: 'No runs match these filters',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'envs-none',
    area: 'Armada',
    where: 'Synthetics, Environments',
    title: 'No environments yet',
    size: 'page',
    subject: 'environments',
  },
  {
    id: 'audits-none',
    area: 'Armada',
    where: 'Audits',
    title: 'No audits yet',
    size: 'page',
    subject: 'audits',
  },
  {
    id: 'audits-tab',
    area: 'Armada',
    where: 'Audits, an empty tab',
    title: 'No audits here',
    size: 'page',
    subject: 'audits',
  },
  {
    id: 'audits-nomatch',
    area: 'Armada',
    where: 'Audits, a search or a tab',
    title: 'No audits match your search',
    size: 'page',
    subject: 'noMatch',
  },

  {
    id: 'dashboards-none',
    area: 'Product Analytics',
    where: 'Dashboards',
    title: 'No dashboards yet',
    size: 'page',
    subject: 'dashboards',
  },
  {
    id: 'dashboards-tab',
    area: 'Product Analytics',
    where: 'Dashboards, a tab with none',
    title: 'No dashboards here yet',
    size: 'page',
    subject: 'dashboards',
  },
  {
    id: 'dashboards-nomatch',
    area: 'Product Analytics',
    where: 'Dashboards, a search',
    title: 'No dashboards match your search',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'dashboard-empty',
    area: 'Product Analytics',
    where: 'A dashboard with no card',
    title: 'Nothing on this dashboard yet',
    size: 'page',
    subject: 'dashboardEmpty',
  },
  {
    id: 'cards-none',
    area: 'Product Analytics',
    where: 'Cards',
    title: 'No cards yet',
    size: 'page',
    subject: 'cards',
  },
  {
    id: 'cards-category',
    area: 'Product Analytics',
    where: 'Cards, a category with none',
    title: 'No cards in this category',
    size: 'page',
    subject: 'cards',
  },
  {
    id: 'cards-nomatch',
    area: 'Product Analytics',
    where: 'Cards, a search or a category',
    title: 'No cards match your search',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'card-nodata',
    area: 'Product Analytics',
    where: 'A card, nothing in the window',
    title: 'No data in this window',
    size: 'page',
    subject: 'chart',
  },
  {
    id: 'card-nodata-small',
    area: 'Product Analytics',
    where: 'A small card, nothing in the window',
    title: 'No data in this window',
    size: 'compact',
    subject: 'chart',
  },
  // the large drawers: drawn at the page size, contextual, no robot
  {
    id: 'run-capture',
    robot: false,
    area: 'Armada',
    where: 'A Synthetics run, a step with no capture',
    title: 'Nothing was captured yet',
    size: 'page',
    subject: 'runs',
  },
  {
    id: 'run-requests',
    robot: false,
    area: 'Armada',
    where: 'A Synthetics run, its network',
    title: 'No requests were recorded',
    size: 'page',
    subject: 'webhooks',
  },
  {
    id: 'run-logs',
    robot: false,
    area: 'Armada',
    where: 'A Synthetics run, its console',
    title: 'Nothing was logged',
    size: 'page',
    subject: 'activity',
  },
  {
    id: 'drawer-nomatch',
    robot: false,
    area: 'Data Management',
    where: 'An event or a person, a property search',
    title: 'No property matches that',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'card-sessions',
    area: 'Product Analytics',
    where: 'A card, its sessions list',
    title: 'No sessions in this window',
    size: 'compact',
    subject: 'calendar',
  },
  {
    id: 'alerts-none',
    area: 'Product Analytics',
    where: 'Alerts',
    title: 'No alerts yet',
    size: 'page',
    subject: 'alerts',
  },
  {
    id: 'alerts-nomatch',
    area: 'Product Analytics',
    where: 'Alerts, a search',
    title: 'No alerts match your search',
    size: 'page',
    subject: 'noMatch',
  },

  {
    id: 'activity-none',
    area: 'Data Management',
    where: 'Activity',
    title: 'No activity yet',
    size: 'page',
    subject: 'activity',
  },
  {
    id: 'activity-nomatch',
    area: 'Data Management',
    where: 'Activity, a search or filters',
    title: 'No events match these filters',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'events-none',
    area: 'Data Management',
    where: 'Events',
    title: 'No events yet',
    size: 'page',
    subject: 'events',
  },
  {
    id: 'events-kind',
    area: 'Data Management',
    where: 'Events, a kind with none',
    title: 'No events of this kind',
    size: 'page',
    subject: 'events',
  },
  {
    id: 'events-nomatch',
    area: 'Data Management',
    where: 'Events, a search or a kind',
    title: 'No events match your search',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'event-props',
    robot: false,
    area: 'Data Management',
    where: 'An event, its own properties',
    title: 'No properties of your own on this event',
    size: 'compact',
    subject: 'properties',
  },
  {
    id: 'features-none',
    robot: false,
    area: 'Data Management',
    where: 'Features',
    title: 'No features yet',
    size: 'page',
    subject: 'features',
  },
  {
    id: 'features-nomatch',
    area: 'Data Management',
    where: 'Features, a search',
    title: 'No features match your search',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'people-none',
    robot: false,
    area: 'Data Management',
    where: 'People',
    title: 'No people yet',
    size: 'page',
    subject: 'people',
  },
  {
    id: 'people-nomatch',
    area: 'Data Management',
    where: 'People, a search',
    title: 'No people match your search',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'person-events',
    area: 'Data Management',
    where: 'A person, the event log',
    title: 'No events in this window',
    size: 'compact',
    subject: 'calendar',
  },
  {
    id: 'person-recordings',
    area: 'Data Management',
    where: 'A person, recordings',
    title: 'No recordings found',
    size: 'compact',
    subject: 'sessions',
  },
  {
    id: 'properties-none',
    robot: false,
    area: 'Data Management',
    where: 'Properties',
    title: 'No properties yet',
    size: 'page',
    subject: 'properties',
  },
  {
    id: 'properties-nomatch',
    area: 'Data Management',
    where: 'Properties, a search',
    title: 'No properties match your search',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'property-people',
    robot: false,
    area: 'Data Management',
    where: 'A property, who carries it',
    title: 'Nobody carries this property yet',
    size: 'compact',
    subject: 'people',
  },
  {
    id: 'property-events',
    area: 'Data Management',
    where: 'A property, which events send it',
    title: 'No event sends this property',
    size: 'compact',
    subject: 'events',
  },

  {
    id: 'exports-none',
    area: 'Preferences',
    where: 'Exports',
    title: 'Nothing exported yet',
    size: 'page',
    subject: 'exports',
  },
  {
    id: 'metadata-none',
    robot: false,
    area: 'Preferences',
    where: 'Metadata',
    title: 'No keys yet',
    size: 'page',
    subject: 'keys',
  },
  {
    id: 'webhooks-none',
    robot: false,
    area: 'Preferences',
    where: 'Webhooks',
    title: 'No endpoints yet',
    size: 'page',
    subject: 'webhooks',
  },
  {
    id: 'audit-log',
    area: 'Preferences',
    where: 'Audit log, a date window',
    title: 'Nothing in this window',
    size: 'page',
    subject: 'calendar',
  },
  {
    id: 'invoices',
    area: 'Preferences',
    where: 'Billing, invoices',
    title: 'No invoice in this window',
    size: 'page',
    subject: 'invoices',
  },
  {
    id: 'team-nomatch',
    area: 'Preferences',
    where: 'Team, a search',
    title: 'Nobody matches',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'tags-openreplay',
    robot: false,
    area: 'Preferences',
    where: 'Armada, journey tags, every default removed',
    title: 'No tags left in OpenReplay’s set',
    size: 'page',
    subject: 'tags',
  },
  {
    id: 'tags-yours',
    robot: false,
    area: 'Preferences',
    where: 'Armada, journey tags',
    title: 'No tags of your own yet',
    size: 'page',
    subject: 'tags',
  },
  {
    id: 'critical-yours',
    area: 'Preferences',
    where: 'Armada, critical descriptions',
    title: 'You have not described anything yet',
    size: 'page',
    subject: 'critical',
  },
  {
    id: 'critical-none',
    area: 'Preferences',
    where: 'Armada, critical descriptions, all removed',
    title: 'Nothing is described as critical yet',
    size: 'page',
    subject: 'critical',
  },

  {
    id: 'search-nomatch',
    area: 'Everywhere',
    where: 'Any search or filter with no result',
    title: 'Nothing matches',
    size: 'page',
    subject: 'noMatch',
  },
  {
    id: 'no-access',
    area: 'Everywhere',
    where: 'Any section your role cannot open',
    title: 'Your role has no access here',
    size: 'page',
    subject: 'noAccess',
    pose: 'wait',
    side: 'left',
    prop: null,
  },
  {
    id: 'larger-screen',
    robot: false,
    area: 'Everywhere',
    where: 'Card editing, journeys, CoBrowse, Synthetics on a phone',
    title: 'Made for a larger screen',
    size: 'page',
    subject: 'largerScreen',
  },
];

export const SESSION_STATES: EmptyEntry[] = [
  {
    id: 's-install',
    area: 'Recordings',
    where: 'A new project, nothing installed',
    title: 'No sessions yet',
    size: 'page',
    subject: 'sessionsInstall',
    pose: 'work',
    side: 'right',
    prop: 'wrench',
    note: 'It is plugging the tracker in: the cable lies unplugged.',
  },
  {
    id: 's-listening',
    area: 'Recordings',
    where: 'Tracker installed, waiting',
    title: 'Listening for the first session',
    size: 'page',
    subject: 'listening',
    pose: 'sit',
    side: 'left',
    prop: null,
    note: 'Sitting on the card, its antenna listening.',
  },
  {
    id: 's-arriving',
    area: 'Recordings',
    where: 'The first session is being processed',
    title: 'Your first session is coming in',
    size: 'page',
    subject: 'sessionsArriving',
    pose: 'cheer',
    side: 'left',
    prop: null,
    note: 'It cannot wait: it cheers as the bar fills.',
  },
  {
    id: 's-quiet',
    area: 'Recordings',
    where: 'Installed, no data for a while',
    title: 'Your tracker has gone quiet',
    size: 'page',
    subject: 'sessionsQuiet',
    pose: 'sleep',
    side: 'left',
    prop: null,
    note: 'Asleep on a flat line; it wakes when you come close.',
  },
  {
    id: 's-window',
    area: 'Recordings',
    where: 'Nothing in the date window',
    title: 'Nothing in the past 7 days',
    size: 'page',
    subject: 'calendarWait',
    pose: 'wait',
    side: 'right',
    prop: null,
    note: 'Waiting beside the calendar; the stopwatch stands on the ground in front of it.',
  },
  {
    id: 's-filters',
    area: 'Recordings',
    where: 'Filters with no result',
    title: 'No sessions match',
    size: 'page',
    subject: 'noMatch',
    pose: 'peek',
    note: 'Curious, from behind the list.',
  },
  {
    id: 's-bookmarks',
    area: 'Recordings',
    where: 'Bookmarks',
    title: 'Nothing bookmarked yet',
    size: 'page',
    subject: 'bookmarks',
    pose: 'stand',
    side: 'left',
    prop: null,
    note: 'Standing by, looking at the empty card.',
  },
  {
    id: 's-paused',
    area: 'Recordings',
    where: 'Free plan, the limit reached',
    title: 'Recording is paused',
    size: 'page',
    subject: 'sessionsPaused',
    pose: 'sit',
    side: 'right',
    prop: null,
    aura: 'ticks',
    note: 'Sitting it out on the paused card.',
  },
  {
    id: 's-mobile',
    area: 'Recordings',
    where: 'A mobile project, nothing yet',
    title: 'No sessions yet',
    size: 'page',
    subject: 'sessionsMobile',
    pose: 'peek',
    note: 'Peeking over the phone.',
  },
  {
    id: 's-filters-small',
    area: 'Recordings',
    where: 'A compact list, filters with no result',
    title: 'No sessions match',
    size: 'compact',
    subject: 'noMatch',
    pose: 'peek',
  },
  {
    id: 's-quiet-small',
    area: 'Recordings',
    where: 'A person, no recordings',
    title: 'No recordings found',
    size: 'compact',
    subject: 'sessions',
    pose: 'sleep',
    side: 'right',
    prop: null,
  },
];

const BY_ID = new Map(
  [...EMPTY_STATES, ...SESSION_STATES].map((e) => [e.id, e]),
);
export const stateById = (id: string): EmptyEntry | undefined => BY_ID.get(id);

// per language: a call site passes its title already translated
const byTitle = new Map<string, Map<string, EmptyEntry>>();
/** The first entry whose title reads `title` in `lang` (the Sessions page's
 *  posed versions share the base titles, so the first wins). */
export function stateByTitle(
  title: string,
  translate: (s: string) => string = (s) => s,
  lang = '',
): EmptyEntry | undefined {
  let titles = byTitle.get(lang);
  if (!titles) {
    titles = new Map();
    for (const e of [...EMPTY_STATES, ...SESSION_STATES]) {
      const k = translate(e.title);
      if (!titles.has(k)) titles.set(k, e);
    }
    byTitle.set(lang, titles);
  }
  return titles.get(title);
}
